import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import { LAYERS } from '../ui/layers';
import { CONDITIONS, METHODS, cleanRequest, localOf } from '../../utils/handoffs';

/**
 * The request form of a specimen handoff (B6): pickup or drop-off, the
 * find's condition, when it can be handed over, a phone and where to meet.
 * Checked here as the server checks it (utils/handoffs.js cleanRequest);
 * `onSubmit(request)` gets the clean request and may return a promise -- a
 * rejection's message is shown and the dialog stays open.
 *
 * "Meet here" takes `location` ({lat, lng}, the phone's position) as the
 * meeting point -- a meeting place, which the lab sees in place of the
 * collection site; the phone and the meeting details are seen only by the
 * lab member who takes the handoff and collection managers.
 */
export default function HandoffRequestDialog({
  initial = {}, location = null, title, submitLabel, onSubmit, onClose, layer = LAYERS.dialog,
  testId = 'handoff-form',
}) {
  const { t } = useTranslation(['handoffs', 'common']);
  const [form, setForm] = useState(() => ({
    method: initial.method || 'pickup',
    condition: initial.condition || '',
    condition_note: initial.condition_note || '',
    window_from: localOf(initial.window_from),
    window_to: localOf(initial.window_to),
    contact_phone: initial.contact_phone || '',
    meeting_note: initial.meeting_note || '',
    meetHere: !!initial.meeting_point,
  }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [dirty, setDirty] = useState(false);
  const set = key => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: value }));
    setDirty(true);
  };
  // Only a pickup has somewhere to meet: switched to a drop-off, a box ticked
  // earlier (or a point the request had) is not sent along unseen.
  const point = form.method === 'pickup' && form.meetHere ? (initial.meeting_point || location) : null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const { request, errors: found } = cleanRequest({ ...form, meeting_point: point });
    setErrors(found);
    if (!request) return;
    setBusy(true);
    setFailure('');
    try {
      await onSubmit?.(request);
    } catch (err) {
      setFailure(err?.message || t('form.failed'));
      setBusy(false);
    }
  };

  const fieldError = key => errors[key] && (
    <span role="alert" style={st.error} data-testid={`${testId}-error-${key}`}>{t(`form.errors.${errors[key]}`)}</span>
  );

  return (
    <Dialog title={title || t('form.title')} onClose={onClose} busy={busy} dirty={dirty} layer={layer}
      width={480} testId={testId}>
      <form onSubmit={submit} noValidate data-testid={testId}>
        <fieldset style={st.fieldset}>
          <legend style={st.legend}>{t('form.method')}</legend>
          {METHODS.map(method => (
            <label key={method} style={st.radio}>
              <input type="radio" name={`${testId}-method`} value={method} checked={form.method === method}
                onChange={set('method')} data-testid={`${testId}-method-${method}`} />
              <span>
                <span style={st.block}>{t(`method.${method}`)}</span>
                <span style={st.hint}>{t(`methodHint.${method}`)}</span>
              </span>
            </label>
          ))}
          {fieldError('method')}
        </fieldset>

        <label style={st.label}>
          <span>{t('form.condition')}</span>
          <select value={form.condition} onChange={set('condition')} style={st.input}
            data-testid={`${testId}-condition`}>
            <option value="">{t('condition.none')}</option>
            {CONDITIONS.map(c => <option key={c} value={c}>{t(`condition.${c}`)}</option>)}
          </select>
        </label>
        <label style={st.label}>
          <span>{t('form.conditionNote')}</span>
          <input type="text" value={form.condition_note} onChange={set('condition_note')} maxLength={500}
            style={st.input} data-testid={`${testId}-condition-note`} />
          {fieldError('condition_note')}
        </label>

        <div style={st.row}>
          <label style={{ ...st.label, flex: 1 }}>
            <span>{t('form.windowFrom')}</span>
            <input type="datetime-local" value={form.window_from} onChange={set('window_from')} style={st.input}
              data-testid={`${testId}-from`} />
            {fieldError('window_from')}
          </label>
          <label style={{ ...st.label, flex: 1 }}>
            <span>{t('form.windowTo')}</span>
            <input type="datetime-local" value={form.window_to} onChange={set('window_to')} style={st.input}
              data-testid={`${testId}-to`} />
            {fieldError('window_to')}
          </label>
        </div>

        <label style={st.label}>
          <span>{t('form.phone')}</span>
          <input type="tel" value={form.contact_phone} onChange={set('contact_phone')} autoComplete="tel"
            dir="ltr" style={st.input} data-testid={`${testId}-phone`} />
          <span style={st.hint}>{t('form.private')}</span>
          {fieldError('contact_phone')}
        </label>
        {form.method === 'pickup' && (location || initial.meeting_point) && (
          <label style={st.check}>
            <input type="checkbox" checked={form.meetHere} onChange={set('meetHere')}
              data-testid={`${testId}-meet-here`} />
            <span>{t('form.meetHere')}</span>
          </label>
        )}
        <label style={st.label}>
          <span>{t('form.meetingNote')}</span>
          <textarea value={form.meeting_note} onChange={set('meeting_note')} rows={2} maxLength={500}
            style={st.input} data-testid={`${testId}-meeting-note`} />
          {fieldError('meeting_note')}
        </label>

        <p style={st.advice} data-testid={`${testId}-handling`}>{t('handling')}</p>
        {failure && <p role="alert" style={st.failure} data-testid={`${testId}-failure`}>{failure}</p>}
        <ActionBar>
          <Button onClick={onClose} disabled={busy} title={t('tip.cancel')}>{t('common:cancel')}</Button>
          <Button type="submit" variant="primary" disabled={busy} title={t('tip.ask')}
            data-testid={`${testId}-submit`}>
            {busy ? t('form.saving') : (submitLabel || t('form.save'))}
          </Button>
        </ActionBar>
      </form>
    </Dialog>
  );
}

const st = {
  fieldset: { border: 'none', padding: 0, margin: '0 0 12px' },
  legend: { fontWeight: 600, marginBottom: 6, padding: 0 },
  radio: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' },
  block: { display: 'block' },
  hint: { display: 'block', fontSize: 13, color: 'var(--c-text-muted-fg)' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, fontSize: 14 },
  check: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 14, cursor: 'pointer' },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--c-border)', font: 'inherit',
    background: 'var(--c-surface)', color: 'var(--c-surface-fg)', minHeight: 40 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  error: { fontSize: 13, color: 'var(--c-danger-fg)' },
  advice: { fontSize: 13, color: 'var(--c-text-muted-fg)', background: 'var(--c-info-soft)', padding: '8px 10px',
    borderRadius: 6, margin: '4px 0 12px' },
  failure: { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', padding: '8px 10px',
    borderRadius: 6, margin: '0 0 12px' },
};
